import { Fragment } from "react";
import styled from "styled-components";
import { capitalize } from "lodash";
import { Stack, Typography } from "@mui/material";
// @ts-ignore
import { Markup } from "react-render-markup";
import Accordion from "../../components/Accordion";
import placeholder from "../../assets/images/placeholder.webp";
import { Flex, Grid, GridItem } from "../../components/Layout";
import { Image as ImageComp } from "../../components";
import TextLink from "../../components/TextLink";
import { User } from "../../types/user";
import { useAppState } from "../../state";
import { getAvailableSizes, shippingCostsForCountry, sizeLabelsForCountry } from "../../helpers/product";
import { ProductPageProductFragment } from "../../generated/storefront";
import { colors, fonts } from "../../theme";
import { FrameType } from "../../helpers/basket";
// import XmasLastDeliveryDates from "../../components/XmasLastDeliveryDates";

type Props = {
  product: ProductPageProductFragment;
  artist?: User | null;
  setItemIsOpen: (isOpen: boolean) => void;
  material: "canvas" | "print";
};

const PaperGrid = styled(Grid)`
  margin-left: -8px;

  > div {
    padding: 4px 8px;
  }
`;

const InfoAccordion = ({ product, artist, setItemIsOpen, material }: Props) => {
  const { isMobileScreen, selectedCountry, toggleGalleryWallBuilderIsOpen } = useAppState();
  const availabileSizes = getAvailableSizes(product.variants.nodes);
  const sizeLabels = sizeLabelsForCountry(selectedCountry);
  const shippingCosts = shippingCostsForCountry(selectedCountry);

  const checkOpenItems = () => {
    const openItems = document.querySelector(".open");
    setItemIsOpen(Boolean(openItems));
  };

  return (
    <Accordion
      onItemOpen={checkOpenItems}
      items={[
        ...(product.descriptionHtml
          ? [
              {
                label: "About the piece",
                content: (
                  <Stack gap={2} paddingX={{ xs: 3, md: 4 }} paddingBottom={4}>
                    <Typography color={colors.grey80} fontFamily={fonts.body}>
                      <Markup markup={product.descriptionHtml} style={{ margin: 0 }} />
                    </Typography>
                  </Stack>
                ),
              },
            ]
          : []),
        {
          label: "Delivery and returns",
          content: (
            <Stack gap={2} paddingX={{ xs: 3, md: 4 }} paddingBottom={4}>
              {/* <XmasLastDeliveryDates /> */}
              <Typography color={colors.grey80} fontFamily={fonts.body}>
                We typically deliver within 5 working days in the UK & EU and 8 working days in the US & Australia, with no import
                duties or taxes. For more details and further information on ordering to other countries, please read our{" "}
                <TextLink to="/delivery" openInNewTab>
                  delivery policy
                </TextLink>
                .
              </Typography>
              <Typography color={colors.grey80} fontFamily={fonts.body}>
                In the UK, EU, US & Australia we charge {shippingCosts.unframed} for an unframed print and {shippingCosts.framed}{" "}
                for a framed print. We charge more for other destinations, depending where you are purchasing.
              </Typography>
              <Typography color={colors.grey80} fontFamily={fonts.body}>
                We offer returns within 30 days of delivery, for more info please read our{" "}
                <TextLink to="/delivery" openInNewTab>
                  returns policy
                </TextLink>
                .
              </Typography>
              <Typography color={colors.grey80} fontFamily={fonts.body}>
                In the unlikely event of an item being delivered damaged or faulty, please contact us on{" "}
                <TextLink href="mailto:hello@goodmoodprints.io">hello@goodmoodprints.io</TextLink> and we will resolve it
                immedietely.
              </Typography>
            </Stack>
          ),
        },

        ...[
          ...(artist
            ? [
                {
                  label: "About the artist",
                  content: (
                    <Stack direction="row" gap={2} paddingX={{ xs: 3, md: 4 }} paddingBottom={4} alignItems="center">
                      <div>
                        <ImageComp src={artist.image || placeholder} radius="100%" height="100px" width="100px" />
                      </div>
                      <Stack gap={1} alignItems={artist.about ? "normal" : "center"}>
                        <TextLink to={`/artists/${artist.permalink || artist.id}`}>
                          <>
                            {artist.firstName} {artist.lastName}
                          </>
                        </TextLink>
                        <Typography color={colors.grey80} fontFamily={fonts.body}>
                          {artist.about}
                        </Typography>
                      </Stack>
                    </Stack>
                  ),
                },
              ]
            : []),
          {
            label: "Specifications",
            content: (
              <Stack gap={2} paddingX={{ xs: 3, md: 4 }} paddingBottom={4}>
                <Typography color={colors.grey80} fontFamily={fonts.body}>
                  {material === "canvas"
                    ? "Our Premium Canvas—where art meets acoustics in the most stunning way. Each canvas is meticulously crafted with a 38mm deep stretch for that luxurious, gallery-quality feel. Not only will it elevate the visual appeal of your room, but it also subtly absorbs sound, creating a serene atmosphere that's perfect for any space."
                    : "We print using industry leading Giclée printers and archival grade 200gsm matte paper, printing in up to individual 12 colours leading to museum level quality prints."}
                </Typography>
                {material === "canvas" && (
                  <Typography color={colors.grey80} fontFamily={fonts.body}>
                    Whether you're enhancing your living room, office, or studio, this canvas wrap offers rich texture and depth,
                    adding a dynamic visual that draws you in. The deep stretch gives it an upscale, sophisticated look, while the
                    acoustic properties ensure a quieter, more peaceful environment.
                  </Typography>
                )}
                <Flex gap={24}>
                  <PaperGrid columns={2}>
                    <GridItem span={2} outline={false}>
                      <Typography color={colors.grey80} fontFamily={fonts.body} fontSize={14}>
                        {material === "canvas" ? "Stretched canvas sizes" : "Paper sizes"}
                      </Typography>
                    </GridItem>
                    {availabileSizes.map((size) => (
                      <Fragment key={size}>
                        <GridItem outline={false}>
                          <Typography color={colors.grey80} fontFamily={fonts.body} fontSize={14}>
                            {sizeLabels[size].shortHand}
                          </Typography>
                        </GridItem>
                        <GridItem outline={false}>
                          <Typography color={colors.grey80} fontFamily={fonts.body} fontSize={14}>
                            {sizeLabels[size].printMeasurements}
                          </Typography>
                        </GridItem>
                      </Fragment>
                    ))}
                  </PaperGrid>

                  <PaperGrid columns={2}>
                    <GridItem span={2} outline={false}>
                      <Typography color={colors.grey80} fontFamily={fonts.body} fontSize={14}>
                        {material === "canvas" ? "Framed canvas sizes" : "Framed print sizes"}
                      </Typography>
                    </GridItem>
                    {availabileSizes.map((size) => (
                      <Fragment key={size}>
                        <GridItem outline={false}>
                          <Typography color={colors.grey80} fontFamily={fonts.body} fontSize={14}>
                            {sizeLabels[size].shortHand}
                          </Typography>
                        </GridItem>
                        <GridItem outline={false}>
                          <Typography color={colors.grey80} fontFamily={fonts.body} fontSize={14}>
                            {sizeLabels[size].framedMeasurements}
                          </Typography>
                        </GridItem>
                      </Fragment>
                    ))}
                  </PaperGrid>
                </Flex>
                <Typography color={colors.grey80} fontFamily={fonts.body}>
                  Our frames are created from high-quality wood, milled with simple clean lines and presented with a satin finish.
                  {material === "print" && "They have a perspex glaze offering great sunlight resistance, protecting your print."}
                </Typography>
                <ul style={{ margin: 0, paddingLeft: "15px" }}>
                  <li>
                    <Typography color={colors.grey80} fontFamily={fonts.body}>
                      {material === "canvas" ? (
                        "38mm deep stretch for a bold, gallery-ready look. Choose framed or unframed, gallery wrapped with a mirror wrap"
                      ) : (
                        <>
                          Arrives ready to hang (view hanging guide <TextLink to="/hanging-guide">here</TextLink>)
                        </>
                      )}
                    </Typography>
                  </li>
                  <li>
                    <Typography color={colors.grey80} fontFamily={fonts.body}>
                      {material === "canvas" ? (
                        "Sound-absorbing design to improve acoustics and reduce noise."
                      ) : (
                        <>
                          {capitalize(FrameType.Standard)} Frame profile: 20mm (0.8") x 23mm (0.9") (front face x depth from wall)
                        </>
                      )}
                    </Typography>
                  </li>
                  <li>
                    <Typography color={colors.grey80} fontFamily={fonts.body}>
                      {material === "print" && capitalize(FrameType.Premium) + " "}Frame profile: 20mm (0.8") x 33mm (1.3") (front
                      face x depth from wall)
                    </Typography>
                  </li>
                  <li>
                    <Typography color={colors.grey80} fontFamily={fonts.body}>
                      Handcrafted from sustainably sourced wood
                    </Typography>
                  </li>
                  <li>
                    <Typography color={colors.grey80} fontFamily={fonts.body}>
                      {material === "canvas"
                        ? "Museum-quality craftsmanship for a finish that's second to none"
                        : "Satin laminated finish for added protection"}
                    </Typography>
                  </li>
                  <li>
                    <Typography color={colors.grey80} fontFamily={fonts.body}>
                      {material === "canvas"
                        ? "Available in a variety of sizes to fit your space perfectly"
                        : "Mounting: adds a 2 inch, off white mount (2.4mm thick) around the print, reducing the size of the print by 2 inches on all sides"}
                    </Typography>
                  </li>
                </ul>
              </Stack>
            ),
          },
          ...(isMobileScreen
            ? []
            : [
                {
                  label: "Want to view it in your own space?",
                  content: (
                    <Stack gap={2} paddingX={{ xs: 3, md: 4 }} paddingBottom={4}>
                      <Typography color={colors.grey80} fontFamily={fonts.body}>
                        Upload a picture to our gallery wall builder{" "}
                        <TextLink onClick={toggleGalleryWallBuilderIsOpen}>here</TextLink>
                      </Typography>
                    </Stack>
                  ),
                },
              ]),
        ],
      ]}
    />
  );
};

export default InfoAccordion;
